import React from 'react';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';

const SEO = ({
  title = null,
  description = null,
  image = null,
  article = false,
  children,
}: {
  title: string | null;
  description: string | null;
  image: string | null;
  article?: boolean;
  children?: React.ReactNode;
}) => {
  const { pathname } = useLocation();
  const { site } = useStaticQuery(query);

  const {
    title: defaultTite,
    description: defaultDescription,
    url: siteUrl,
    image: defaultImage,
    twitterUsername,
    siteLanguage,
    author,
  } = site.siteMetadata;
  const buildTime = site.buildTime;

  const seo = {
    title: title || defaultTite,
    description: description || defaultDescription,
    image: `${image || defaultImage}`,
    url: `${siteUrl}${pathname}`,
  };

  const schemaOrgWebPage = {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    url: siteUrl,
    headline: description || defaultDescription,
    inLanguage: siteLanguage,
    mainEntityOfPage: siteUrl,
    description: defaultDescription,
    name: title || defaultTite,
    datePublished: '2021-05-15T00:00:00+01:00',
    dateModified: buildTime,
    image: {
      '@type': 'ImageObject',
      url: `${siteUrl}${image || defaultImage}`,
    },
  };

  const itemListElement = [
    {
      '@type': 'ListItem',
      item: {
        '@id': siteUrl,
        name: 'Homepage',
      },
      position: 1,
    },
  ];

  let schemaArticle = null;

  if (article) {
    schemaArticle = {
      '@context': 'http://schema.org',
      '@type': 'Article',
      author: {
        '@type': 'Person',
        name: author,
      },
      copyrightHolder: {
        '@type': 'Person',
        name: author,
      },
      copyrightYear: '2022',
      creator: {
        '@type': 'Person',
        name: author,
      },
      publisher: {
        '@type': 'Organization',
        name: author,
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}${image || defaultImage}`,
        },
      },
      description: seo.description,
      headline: seo.title,
      inLanguage: siteLanguage,
      url: seo.url,
      name: seo.title,
      image: {
        '@type': 'ImageObject',
        url: seo.image,
      },
      mainEntityOfPage: seo.url,
    };
  }

  // Push current blogpost into breadcrumb list
  itemListElement.push({
    '@type': 'ListItem',
    item: {
      '@id': seo.url,
      name: seo.title,
    },
    position: 2,
  });

  const breadcrumb = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    description: 'Breadcrumbs list',
    name: 'Breadcrumbs',
    itemListElement,
  };

  return (
    <>
      <title>{seo.title} · Sentí lo nuestro</title>
      <meta content={seo.description} name="description" />
      <meta content={seo.image} name="image" />
      {seo.url && <meta content={seo.url} name="og:url" />}
      {(article ? true : null) && <meta content="article" name="og:type" />}
      {seo.title && <meta content={seo.title} name="og:title" />}
      {seo.description && <meta content={seo.description} property="og:description" />}
      {seo.image && <meta content={seo.image} property="og:image" />}
      <meta content="summary_large_image" property="twitter:card" />
      {twitterUsername && <meta content={twitterUsername} property="twitter:creator" />}
      {seo.title && <meta content={seo.title} property="twitter:title" />}
      {seo.description && <meta content={seo.description} property="twitter:description" />}
      {seo.image && <meta content={seo.image} property="twitter:image" />}
      {!article && <script type="application/ld+json">{JSON.stringify(schemaOrgWebPage)}</script>}
      {article && <script type="application/ld+json">{JSON.stringify(schemaArticle)}</script>}
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      {children}
    </>
  );
};

export default SEO;

const query = graphql`
  query SEO {
    site {
      buildTime
      siteMetadata {
        defaultTitle: title
        defaultDescription: description
        url
        defaultImage: image
        twitterUsername
        author
      }
    }
  }
`;
